import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

interface Props { settings?: StoreSettings; storeId: string; }

export function ClientAuthTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [requireEmail, setRequireEmail] = useState(false);
  const [requirePhone, setRequirePhone] = useState(false);
  const [allowGuest, setAllowGuest] = useState(true);

  useEffect(() => {
    if (settings?.clientsAuthentication) {
      const auth = settings.clientsAuthentication;
      setEnabled(auth.enabled ?? false);
      setRequireEmail(auth.requireEmail ?? false);
      setRequirePhone(auth.requirePhone ?? false);
      setAllowGuest(auth.allowGuest ?? true);
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () => settingsApi.updateClientAuth(storeId, { enabled, requireEmail, requirePhone, allowGuest }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settings.clientAuth')}</h2>
        <Toggle label={t('settings.enableAuth')} description={t('settings.enableAuthDesc')} checked={enabled} onChange={setEnabled} />
        <Toggle label={t('settings.requireEmail')} checked={requireEmail} onChange={setRequireEmail} />
        <Toggle label={t('settings.requirePhone')} checked={requirePhone} onChange={setRequirePhone} />
        <Toggle label={t('settings.allowGuest')} description={t('settings.allowGuestDesc')} checked={allowGuest} onChange={setAllowGuest} />
      </section>
      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
